import { atom, Atom, WritableAtom } from 'jotai';
import { debounce } from 'lodash';

type AnyAtom = Atom<any>;

export function createDebouncedDerivedAtom<T extends AnyAtom[], R>(
    baseAtoms: [...T],
    deriveFn: (...values: { [K in keyof T]: T[K] extends Atom<infer U> ? U : never }) => R,
    debounceTime: number = 200,
    options: { leading?: boolean; trailing?: boolean } = { leading: true, trailing: true }
): Atom<R> {
    const derivedAtom = atom(
        (get) => {
            const values = baseAtoms.map(get) as { [K in keyof T]: T[K] extends Atom<infer U> ? U : never };
            return deriveFn(...values);
        }
    );

    const debouncedUpdate = debounce((get: Parameters<typeof derivedAtom.read>[0], set: Parameters<WritableAtom<R, [R], void>['write']>[1]) => {
        set(derivedAtom as WritableAtom<R, [R], void>, get(derivedAtom));
    }, debounceTime, options);

    const debouncedAtom = atom(
        (get) => get(derivedAtom),
        (get, set) => {
            debouncedUpdate(get, set);
        }
    );

    return debouncedAtom;
}
